// =============================================================================
// components/nav/FloatingTabBar.tsx — CRYB floating glass bottom navigation.
//
// An Instagram-style floating nav: a detached GlassBar (the glass primitive — glass
// is NEVER re-authored here) that hovers above the screen bottom with safe-area-aware
// padding, rounded to glassTokens.radius.xl. Screen content renders edge-to-edge
// UNDERNEATH it because the bottom-tabs navigator mounts this as an absolutely-
// positioned custom `tabBar` (set `tabBarStyle:{ position:'absolute' }` so
// react-navigation stops reserving layout space for the bar — each screen's scroll
// view then fills the full height and scrolls behind the glass). Every screen pads
// its scroll content by layout.bottomNavInset (or useTabBarClearance()) so the last
// row clears it.
//
// Brand: lavender glass surface (from tokens), primary #6C5CE7 active state with a
// filled icon + tinted label. Press uses the spring (scale + brightness dip): each
// tab is a Pressable with a shared Animated spring — the bar stays a single GlassBar
// and we animate icons, so the whole row reads as one pane of glass (Instagram),
// not four separate chips.
//
// Responsive: respects getLayout() — full width on phone, centred + capped on
// tablet/desktop.
//
// BARE-RN MIGRATION (blueprint §1 + §2):
//   - This was an expo-router `<Tabs tabBar={...}>` custom bar. It is now a
//     react-navigation BOTTOM-TABS custom tabBar: it receives the real
//     `BottomTabBarProps` (state/descriptors/navigation/insets). Wire it in the
//     Tab.Navigator as:  tabBar={(props) => <FloatingTabBar {...props} />}
//     with screenOptions={{ headerShown:false, tabBarStyle:{ position:'absolute' } }}.
//   - @expo/vector-icons Ionicons → local Icon shim.
//   - DM badge reads the unread total via ../nav/dmBadge (a tolerant bridge to
//     services/dmState, ported by the services agent — see dmBadge.ts).
//   - ICONS are keyed by the navigator's PascalCase route names (Home/Markets/
//     Discover/Chat/Profile) — see navigation/types.ts TabsParamList.
//   - 5-TAB BAR (client reference): Home · Communities · DISCOVER (centre,
//     compass) · Chat · Profile. Items are flex:1 with ≥44pt hit targets.
//   - The centre "+" Create tab was REMOVED: creating starts with the Home
//     tab's right-edge left-swipe → full-screen CreateHub (components/create/),
//     which routes into the CreatePost/StoryCreate root modals.
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, IoniconName } from '../ui/Icon';
import { GlassBar, glassTokens } from '../glass';
import { colors, getLayout, spacing } from '../../constants/theme';
import { getDmUnreadTotal, onDmUnreadChange } from './dmBadge';

// The custom tabBar receives react-navigation's BottomTabBarProps verbatim.
export type FloatingTabBarProps = BottomTabBarProps;

// Icon set per route name (filled when active, outline when inactive) — the
// Instagram pattern. Keyed by the bottom-tabs navigator's PascalCase route
// names (TabsParamList).
type IconPair = { active: IoniconName; inactive: IoniconName };
const ICONS: Record<string, IconPair> = {
  Home: { active: 'home', inactive: 'home-outline' },
  // Discover — left of centre (compass). Communities live inside it now.
  Discover: { active: 'compass', inactive: 'compass-outline' },
  // Learn — the CENTRE tab (learning center).
  Learn: { active: 'school', inactive: 'school-outline' },
  Chat: { active: 'chatbubble-ellipses', inactive: 'chatbubble-ellipses-outline' },
  Profile: { active: 'person', inactive: 'person-outline' },
};
// The route whose icon carries the live DM unread badge.
const BADGE_ROUTE = 'Chat';

// Max width of the floating bar per device class (centres + constrains on tablet/
// iPad/desktop, full width on phone). Mirrors Screen/maxWidthFor brand values.
function barMaxWidth(width: number): number | undefined {
  const l = getLayout(width);
  if (l === 'desktop') return 480;
  if (l === 'tablet') return 440;
  return undefined; // phone: full width (minus side margins)
}

function TabItem({
  routeName,
  focused,
  label,
  badge,
  onPress,
  onLongPress,
}: {
  routeName: string;
  focused: boolean;
  label: string;
  // Live unread count for this tab (currently only the chat/DM tab). 0 = no dot.
  badge?: number;
  onPress: () => void;
  onLongPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;

  const pressIn = () =>
    Animated.spring(scale, {
      toValue: glassTokens.pressScale,
      useNativeDriver: true,
      speed: 50,
      bounciness: 0,
    }).start();
  const pressOut = () =>
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
      speed: 40,
      bounciness: 6,
    }).start();

  const pair = ICONS[routeName] ?? { active: 'ellipse', inactive: 'ellipse-outline' };
  const tint = focused ? colors.primary : colors.muted;

  return (
    <Animated.View style={[styles.item, { transform: [{ scale }] }]}>
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        onPressIn={pressIn}
        onPressOut={pressOut}
        accessibilityRole="button"
        accessibilityState={{ selected: focused }}
        accessibilityLabel={label}
        hitSlop={8}
        style={styles.tabPressable}
      >
        <View>
          <Ionicons
            name={focused ? pair.active : pair.inactive}
            size={21}
            color={tint}
          />
          {!!badge && badge > 0 && (
            <View style={styles.navBadge}>
              <Text style={styles.navBadgeText} numberOfLines={1}>
                {badge > 99 ? '99+' : badge}
              </Text>
            </View>
          )}
        </View>
        {/* Fixed 2-line-tall label box so a long label ("Learning Center") wraps
            to two centered lines while single-word tabs still align on the same
            baseline — no truncation, icons + dots stay level across all 5 tabs. */}
        <View style={styles.labelBox}>
          <Text
            style={[styles.label, { color: tint, fontWeight: focused ? '700' : '600' }]}
            numberOfLines={2}
            textBreakStrategy="balanced"
          >
            {label}
          </Text>
        </View>
        {focused ? <View style={styles.activeDot} /> : <View style={styles.activeDotPlaceholder} />}
      </Pressable>
    </Animated.View>
  );
}

export default function FloatingTabBar({
  state,
  descriptors,
  navigation,
}: FloatingTabBarProps) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const maxWidth = barMaxWidth(width);

  // Live DM unread total → the chat tab badge. Subscribes to the shared dmState
  // store (via the dmBadge bridge) so a message landing in any thread (even
  // off-screen) lights the dot, and opening/reading a thread clears it — no
  // refetch, in real time. (LR2)
  const [dmUnread, setDmUnread] = useState(getDmUnreadTotal());
  useEffect(() => {
    setDmUnread(getDmUnreadTotal());
    return onDmUnreadChange(() => setDmUnread(getDmUnreadTotal()));
  }, []);

  // Detach from the very bottom: safe-area inset + a floating gap. On devices with
  // no home indicator (insets.bottom===0) keep a sensible minimum lift.
  const bottomLift = Math.max(insets.bottom, spacing.md) + spacing.sm;

  return (
    <View
      pointerEvents="box-none"
      style={[styles.host, { paddingBottom: bottomLift }]}
    >
      <GlassBar
        radius={glassTokens.radius.xl}
        style={[
          styles.bar,
          maxWidth ? { maxWidth, width: '100%', alignSelf: 'center' } : null,
        ]}
        contentStyle={styles.barContent}
      >
        {state.routes.map((route, index) => {
          const { options } = descriptors[route.key];
          const focused = state.index === index;
          const label =
            typeof options.tabBarLabel === 'string'
              ? options.tabBarLabel
              : options.title ?? route.name;

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            });
            if (!focused && !event.defaultPrevented) {
              navigation.navigate(route.name as never);
            }
          };
          const onLongPress = () => {
            navigation.emit({ type: 'tabLongPress', target: route.key });
          };

          return (
            <TabItem
              key={route.key}
              routeName={route.name}
              focused={focused}
              label={label}
              badge={route.name === BADGE_ROUTE ? dmUnread : 0}
              onPress={onPress}
              onLongPress={onLongPress}
            />
          );
        })}
      </GlassBar>
    </View>
  );
}

const styles = StyleSheet.create({
  // Overlay that sits at the bottom of the screen above all content. box-none so
  // taps outside the bar fall through to the content scrolling underneath.
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: spacing.lg,
  },
  bar: {
    // GlassBar supplies the glass; we only own layout + the floating shadow lift.
  },
  barContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    // 5 tabs now share the row — tighter gutter so each item keeps a roomy
    // flex:1 slice (≈68pt on a 390pt phone) without shrinking hit targets.
    paddingHorizontal: spacing.xs,
    paddingVertical: 3,
  },
  item: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabPressable: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    // Room for icon (21) + gap + a 2-line label box (24) + active dot. hitSlop 8
    // keeps the effective touch target ≥ HIG even though the visual bar is compact.
    minHeight: 52,
    alignSelf: 'stretch',
    paddingVertical: 2,
    gap: 2,
  },
  // Fixed height = two lines at 10pt/12lh, so every tab reserves the same label
  // space whether its label is one line ("Home") or two ("Learning Center").
  labelBox: {
    height: 24,
    justifyContent: 'center',
  },
  label: {
    // Readable (10pt vs the old truncated 8.5) — wraps to 2 centered lines when
    // long instead of clipping to "Learning Cent…".
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.1,
    textAlign: 'center',
  },
  activeDot: {
    marginTop: 2,
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.primary,
  },
  activeDotPlaceholder: {
    marginTop: 2,
    width: 4,
    height: 4,
  },
  // Live DM unread badge, top-right of the chat tab icon.
  navBadge: {
    position: 'absolute',
    top: -5,
    right: -9,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    backgroundColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBadgeText: {
    fontSize: 9,
    lineHeight: 11,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
